import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Package, Upload } from 'lucide-react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, Timestamp, query, where } from 'firebase/firestore';
import { db, storage } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import ImageUploader from './ImageUploader';
import SmartImage from '../SmartImage';

const EnhancedProductsManagement = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [productImage, setProductImage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [productsSnapshot, categoriesSnapshot] = await Promise.all([
        getDocs(collection(db, COLLECTIONS.PRODUCTS)),
        getDocs(collection(db, COLLECTIONS.CATEGORIES))
      ]);

      const prods = [];
      productsSnapshot.forEach((doc) => {
        prods.push({ id: doc.id, ...doc.data() });
      });

      const cats = [];
      categoriesSnapshot.forEach((doc) => {
        cats.push({ id: doc.id, ...doc.data() });
      });

      setProducts(prods);
      setCategories(cats);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const productData = {
      name: formData.get('name'),
      price: formData.get('price'),
      categoryId: formData.get('categoryId'),
      categoryName: categories.find(c => c.id === formData.get('categoryId'))?.name || '',
      description: formData.get('description'),
      image: formData.get('image') || '📦',
      stock: formData.get('stock') || 'متوفر',
      brand: formData.get('brand') || '',
      features: formData.get('features')?.split(',').map(f => f.trim()) || [],
      active: true,
      createdAt: Timestamp.now()
    };

    try {
      if (editingProduct) {
        // التحقق من وجود المنتج قبل التحديث
        try {
          await updateDoc(doc(db, COLLECTIONS.PRODUCTS, editingProduct.id), productData);
        } catch (updateError) {
          if (updateError.code === 'not-found') {
            alert('هذا المنتج غير موجود في قاعدة البيانات. سيتم إضافته كمنتج جديد.');
            // إضافة المنتج كجديد بدلاً من التحديث
            await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);
          } else {
            throw updateError;
          }
        }
      } else {
        await addDoc(collection(db, COLLECTIONS.PRODUCTS), productData);
      }
      setShowModal(false);
      setEditingProduct(null);
      loadData();
    } catch (error) {
      console.error('Error saving product:', error);
      alert(`حدث خطأ أثناء حفظ المنتج: ${error.message}`);
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المنتج؟')) {
      try {
        await deleteDoc(doc(db, COLLECTIONS.PRODUCTS, productId));
        // إزالة المنتج من القائمة فوراً
        setProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
      } catch (error) {
        console.error('Error deleting product:', error);
        if (error.code === 'not-found') {
          // إذا لم يكن المنتج موجوداً في Firebase، قم بإزالته من القائمة المحلية فقط
          setProducts(prevProducts => prevProducts.filter(p => p.id !== productId));
          alert('تم إزالة المنتج من القائمة (كان غير موجود في قاعدة البيانات)');
        } else {
          alert(`حدث خطأ أثناء الحذف: ${error.message}`);
        }
      }
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || product.categoryId === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">إدارة المنتجات</h1>
          <p className="text-gray-600 mt-2">إجمالي المنتجات: {products.length}</p>
        </div>
        <button
          onClick={() => { setEditingProduct(null); setShowModal(true); }}
          className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          إضافة منتج جديد
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="ابحث عن منتج..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
            />
          </div>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
          >
            <option value="all">جميع التصنيفات</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{String(cat.name || '')}</option>
            ))}
          </select>
        </div>
      </div>

      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => (
            <div key={product.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden">
              <div className="p-6">
                <div className="h-24 flex items-center justify-center mb-4">
                  <SmartImage
                    src={product.image}
                    alt={product.name}
                    size="large"
                    fallback="📦"
                  />
                </div>
                <div className="mb-2">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">{String(product.categoryName || '')}</span>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">{String(product.name || '')}</h3>
                <p className="text-2xl font-bold text-yellow-600 text-center mb-2">{String(product.price || '')}</p>
                {product.brand && <p className="text-sm text-gray-600 text-center mb-2">العلامة: {String(product.brand)}</p>}
                <p className="text-sm text-gray-600 text-center mb-4">{String(product.stock || 'متوفر')}</p>
                <div className="flex gap-2">
                  <button onClick={() => { setEditingProduct(product); setShowModal(true); }} className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors">
                    <Edit className="w-4 h-4" />تعديل
                  </button>
                  <button onClick={() => handleDelete(product.id)} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors">
                    <Trash2 className="w-4 h-4" />حذف
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>لا توجد منتجات متاحة</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 my-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">{editingProduct ? 'تعديل المنتج' : 'إضافة منتج جديد'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">اسم المنتج</label>
                  <input type="text" name="name" defaultValue={editingProduct?.name} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">السعر</label>
                  <input type="text" name="price" defaultValue={editingProduct?.price} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">التصنيف</label>
                  <select name="categoryId" defaultValue={editingProduct?.categoryId} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right">
                    <option value="">اختر التصنيف</option>
                    {categories.map((cat) => (<option key={cat.id} value={cat.id}>{String(cat.name || '')}</option>))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">العلامة التجارية</label>
                  <input type="text" name="brand" defaultValue={editingProduct?.brand} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" />
                </div>
              </div>
              <ImageUploader
                currentImage={editingProduct?.image || productImage}
                onImageChange={setProductImage}
                label="صورة المنتج"
              />
              <input type="hidden" name="image" value={productImage} />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">الوصف</label>
                <textarea name="description" defaultValue={editingProduct?.description} rows="3" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">المميزات (افصل بفاصلة)</label>
                <input type="text" name="features" defaultValue={editingProduct?.features?.join(', ')} placeholder="ميزة 1, ميزة 2, ميزة 3" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right" />
              </div>
              <div className="flex gap-3 mt-6">
                <button type="submit" className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg font-medium transition-colors">{editingProduct ? 'حفظ التعديلات' : 'إضافة المنتج'}</button>
                <button type="button" onClick={() => { setShowModal(false); setEditingProduct(null); }} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-3 rounded-lg font-medium transition-colors">إلغاء</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedProductsManagement;
